import React from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { Box, Button, Divider, FormHelperText, InputLabel, MenuItem, OutlinedInput, Select, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PageHeader from '../../../components/page-header';
import { useBudgetActionCreateEditController } from './context/budget-action-create-edit-controller';
import { useBudgetActionCreateEditState } from './context/budget-action-create-edit-context';

const BUDGET_ACTION_TYPES = [
    { id: 1, label: 'Projeto' },
    { id: 2, label: 'Atividade' },
];

export function BudgetActionCreateEditComponent() {
    const { handleOnChange, handleBudgetUnitChange, handleSave, handleCancel, isSaving } = useBudgetActionCreateEditController();
    const { budgetActionRequest, selectedBudgetUnitId, budgetUnits, budgetPrograms, showValidationErrors, budgetActionId } = useBudgetActionCreateEditState();
    const isEdit = !!budgetActionId;

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title={isEdit ? 'Editar Ação Orçamentária' : 'Nova Ação Orçamentária'}
                    subtitle={isEdit ? 'Atualize as informações da ação orçamentária' : 'Cadastre uma nova ação orçamentária no sistema'}
                    breadcrumbs={[{ label: 'Ações Orçamentárias', href: '/acoes-orcamentarias' }, { label: isEdit ? 'Editar' : 'Nova' }]}
                />
            </Grid>

            <Grid item xs={12}>
                <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }} elevation={3}>
                    <Grid container spacing={3}>

                        {/* Identificação */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                                Identificação
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <InputLabel required>Código</InputLabel>
                            <OutlinedInput
                                fullWidth
                                autoComplete="off"
                                name="code"
                                value={budgetActionRequest.code || ''}
                                onChange={handleOnChange}
                                error={showValidationErrors && !budgetActionRequest.code}
                            />
                            {showValidationErrors && !budgetActionRequest.code && (
                                <FormHelperText error>Código é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <InputLabel required>Nome</InputLabel>
                            <OutlinedInput
                                fullWidth
                                autoComplete="off"
                                name="name"
                                value={budgetActionRequest.name || ''}
                                onChange={handleOnChange}
                                error={showValidationErrors && !budgetActionRequest.name}
                            />
                            {showValidationErrors && !budgetActionRequest.name && (
                                <FormHelperText error>Nome é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        {/* Classificação */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, mt: 1, color: 'primary.main', fontWeight: 600 }}>
                                Classificação
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                        </Grid>

                        <Grid item xs={12} md={12}>
                            <InputLabel required>Unidade Orçamentária</InputLabel>
                            <Select
                                fullWidth
                                name="selectedBudgetUnitId"
                                disabled={isEdit}
                                value={selectedBudgetUnitId ?? ''}
                                onChange={handleBudgetUnitChange}
                                error={showValidationErrors && !selectedBudgetUnitId}
                            >
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {budgetUnits.map((u) => <MenuItem key={u.id} value={u.id}>{u.code} - {u.name}</MenuItem>)}
                            </Select>
                            {showValidationErrors && !selectedBudgetUnitId && (
                                <FormHelperText error>Unidade Orçamentária é obrigatória</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <InputLabel required>Programa Orçamentário</InputLabel>
                            <Select
                                fullWidth
                                name="budgetProgramId"
                                disabled={isEdit || !selectedBudgetUnitId}
                                value={budgetActionRequest.budgetProgramId ?? ''}
                                onChange={handleOnChange}
                                error={showValidationErrors && !budgetActionRequest.budgetProgramId}
                            >
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {budgetPrograms.map((p) => <MenuItem key={p.id} value={p.id}>{p.code} - {p.name}</MenuItem>)}
                            </Select>
                            {showValidationErrors && !budgetActionRequest.budgetProgramId && (
                                <FormHelperText error>Programa Orçamentário é obrigatório</FormHelperText>
                            )}
                            {!selectedBudgetUnitId && (
                                <FormHelperText>Selecione a Unidade Orçamentária para listar os programas</FormHelperText>
                            )}
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <InputLabel required>Tipo</InputLabel>
                            <Select
                                fullWidth
                                name="actionType"
                                value={budgetActionRequest.actionType ?? ''}
                                onChange={handleOnChange}
                                error={showValidationErrors && !budgetActionRequest.actionType}
                            >
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {BUDGET_ACTION_TYPES.map((t) => <MenuItem key={t.id} value={t.id}>{t.id} - {t.label}</MenuItem>)}
                            </Select>
                            {showValidationErrors && !budgetActionRequest.actionType && (
                                <FormHelperText error>Tipo é obrigatório</FormHelperText>
                            )}
                        </Grid>

                        {/* Ações */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, mt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ flex: '1 1 auto' }} />
                                <Button
                                    variant="outlined"
                                    startIcon={<CancelIcon />}
                                    onClick={handleCancel}
                                    sx={{ mr: 1 }}
                                >
                                    Cancelar
                                </Button>
                                <LoadingButton
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSave}
                                    loading={isSaving}
                                    disabled={isSaving}
                                >
                                    Salvar
                                </LoadingButton>
                            </Box>
                        </Grid>

                    </Grid>
                </Paper>
            </Grid>
        </React.Fragment>
    );
}
